/**
 * Shared review primitives for the SmartImprove consultant workspace.
 *
 * Every generated entity (insight, recommendation, roi, implementation,
 * exec_summary, transformation_mapping) carries the same audit + review
 * shape from the Phase 3 orchestrator — `status`, `override_fields`,
 * `regenerated_count`, etc. These primitives render that shape consistently
 * across all 5 tabs, plus expose Accept / Reject / Edit / Regenerate actions
 * backed by the existing API:
 *
 *   PUT  /api/smartimprove/generated/{entity}/{id}        — edit
 *   POST /api/smartimprove/generated/{entity}/{id}/regenerate
 *   POST /api/smartimprove/generated/{entity}/{id}/accept
 *   POST /api/smartimprove/generated/{entity}/{id}/reject
 *
 * Visual language matches the existing SmartEvaluate / SmartImprove cards.
 */
import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../components/ui/dialog";
import {
  Check,
  X,
  Pencil,
  RefreshCw,
  ShieldCheck,
  CircleSlash,
  Edit3,
  CircleDot,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/smartimprove`;
export const ax = axios.create({ baseURL: API, withCredentials: true });

const STATUS_PILL = {
  draft: "bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]",
  edited: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
  accepted: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
  rejected: "bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]",
};

const STATUS_ICON = {
  draft: CircleDot,
  edited: Edit3,
  accepted: ShieldCheck,
  rejected: CircleSlash,
};

export function StatusPill({ status, regenerated_count }) {
  const cls = STATUS_PILL[status] || STATUS_PILL.draft;
  const Icon = STATUS_ICON[status] || CircleDot;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold border rounded-sm capitalize ${cls}`}
      data-testid={`status-pill-${status}`}
    >
      <Icon className="w-3 h-3" />
      {status}
      {regenerated_count ? (
        <span className="ml-1 text-[10px] opacity-70">· r{regenerated_count}</span>
      ) : null}
    </span>
  );
}

const SEVERITY_PILL = {
  low: "bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]",
  medium: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
  high: "bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]",
  critical: "bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]",
};

export function SeverityPill({ value }) {
  if (!value) return null;
  const cls = SEVERITY_PILL[value] || "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]";
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm capitalize ${cls}`}
    >
      {value}
    </span>
  );
}

export function ComplexityPill({ value }) {
  if (!value) return null;
  const map = {
    low: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
    medium: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
    high: "bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]",
  };
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm capitalize ${
        map[value] || "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]"
      }`}
    >
      {value}
    </span>
  );
}

export function AreaTag({ value }) {
  if (!value) return null;
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold border border-[#E5E7EB] bg-[#F8FAFC] text-[#374151] rounded-sm capitalize">
      {String(value).replace(/_/g, " ")}
    </span>
  );
}

/**
 * Accept / Reject / Edit / Regenerate buttons. Regenerate is only enabled for
 * `insight` and `recommendation` entities (per the Phase 3 orchestrator).
 */
export function ReviewActions({
  entity,
  row,
  onEdit,
  onRefresh,
  allowRegenerate = true,
  isFinalized = false,
}) {
  const [busy, setBusy] = useState(null);
  const call = async (action, label) => {
    setBusy(action);
    try {
      if (action === "regenerate") {
        await ax.post(`/generated/${entity}/${row.id}/regenerate`);
      } else {
        await ax.post(`/generated/${entity}/${row.id}/${action}`);
      }
      toast.success(`${label} applied`);
      onRefresh && (await onRefresh());
    } catch {
      toast.error(`Failed to ${label.toLowerCase()}`);
    } finally {
      setBusy(null);
    }
  };
  if (isFinalized) {
    return (
      <span
        className="text-[10px] font-semibold text-[#92400E] uppercase tracking-wider"
        data-testid={`review-locked-${row.id}`}
      >
        locked (finalized)
      </span>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <button
        onClick={() => call("accept", "Accept")}
        disabled={!!busy || row.status === "accepted"}
        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold border border-[#A7F3D0] bg-[#ECFDF5] text-[#065F46] rounded-sm hover:bg-[#D1FAE5] disabled:opacity-50"
        data-testid={`review-accept-${row.id}`}
        title="Accept"
      >
        <Check className="w-3 h-3" />
        Accept
      </button>
      <button
        onClick={() => call("reject", "Reject")}
        disabled={!!busy || row.status === "rejected"}
        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold border border-[#FECACA] bg-[#FEF2F2] text-[#B91C1C] rounded-sm hover:bg-[#FEE2E2] disabled:opacity-50"
        data-testid={`review-reject-${row.id}`}
        title="Reject"
      >
        <X className="w-3 h-3" />
        Reject
      </button>
      <button
        onClick={() => onEdit(row)}
        disabled={!!busy}
        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold border border-[#E5E7EB] bg-white text-[#374151] rounded-sm hover:bg-[#F8FAFC]"
        data-testid={`review-edit-${row.id}`}
        title="Edit"
      >
        <Pencil className="w-3 h-3" />
        Edit
      </button>
      {allowRegenerate ? (
        <button
          onClick={() => call("regenerate", "Regenerate")}
          disabled={!!busy}
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold border border-[#E5E7EB] bg-white text-[#374151] rounded-sm hover:bg-[#F8FAFC]"
          data-testid={`review-regenerate-${row.id}`}
          title="Regenerate from latest sources (replaces just this row)"
        >
          <RefreshCw className={`w-3 h-3 ${busy === "regenerate" ? "animate-spin" : ""}`} />
          Regen
        </button>
      ) : null}
    </div>
  );
}

/**
 * Lightweight edit dialog for insight + recommendation rows.
 * Allows the consultant to override `title`, `narrative`, and (for
 * recommendations) `expected_business_impact`. Status flips to `edited` and
 * override_fields are recorded server-side.
 */
export function EditDialog({ entity, row, onClose, onRefresh }) {
  const [form, setForm] = useState({
    title: row?.title || "",
    narrative: row?.narrative || "",
    expected_business_impact: row?.expected_business_impact || "",
  });
  const [saving, setSaving] = useState(false);
  if (!row) return null;
  const showImpact = entity === "recommendations";
  const save = async () => {
    setSaving(true);
    try {
      const payload = { title: form.title, narrative: form.narrative };
      if (showImpact) payload.expected_business_impact = form.expected_business_impact;
      await ax.put(`/generated/${entity}/${row.id}`, payload);
      toast.success("Saved");
      onClose();
      await onRefresh();
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => (!o ? onClose() : null)}>
      <DialogContent className="max-w-2xl" data-testid={`edit-dialog-${entity}`}>
        <DialogHeader>
          <DialogTitle>Edit {entity.replace(/s$/, "")}</DialogTitle>
          <DialogDescription>
            Your changes are saved as a consultant override. Status flips to
            <strong> edited</strong> and override fields are recorded in the audit log.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">Title</label>
            <Input
              value={form.title}
              onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
              className="rounded-sm text-sm"
              data-testid="edit-dialog-title"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">Narrative</label>
            <Textarea
              value={form.narrative}
              onChange={(e) => setForm((s) => ({ ...s, narrative: e.target.value }))}
              rows={5}
              className="rounded-sm text-sm"
              data-testid="edit-dialog-narrative"
            />
          </div>
          {showImpact ? (
            <div>
              <label className="block text-xs font-medium text-[#374151] mb-1">
                Expected business impact
              </label>
              <Textarea
                value={form.expected_business_impact}
                onChange={(e) =>
                  setForm((s) => ({ ...s, expected_business_impact: e.target.value }))
                }
                rows={3}
                className="rounded-sm text-sm"
              />
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm text-xs">
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
            data-testid="edit-dialog-save"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EmptyState({ icon: Icon, title, description }) {
  return (
    <div className="bg-white border border-dashed border-[#E5E7EB] rounded-sm px-6 py-10 text-center">
      {Icon ? <Icon className="w-8 h-8 text-[#CBD5E1] mx-auto" strokeWidth={1.5} /> : null}
      <h3 className="text-sm font-semibold text-[#111827] mt-3">{title}</h3>
      {description ? (
        <p className="text-xs text-[#6B7280] mt-1 max-w-md mx-auto">{description}</p>
      ) : null}
    </div>
  );
}
